import React, { useEffect, useState, useRef } from 'react';
import { Button, ScrollIndicator, SkeletonLoader } from '../../../components/uielements';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Actions,
  Card,
  Container,
  FileName,
  Input,
  Main,
  Progress,
  ProgressBar,
  PurchaseButton,
  RivewAndLoadgeButton,
  StatusMessage,
  Title,
  UploadButton,
  Heading,
  ViewButton,
  ActionContainer,
  DeleteButton,
} from './docUpload.style';
import PopUp from './popup';

import { getItem } from '../../../helper/localStorageHelper';
import { getPdfListForUpload, uploadPdf, deleteUploadedDoc } from '../../../services/uploadDoc';
import { useScrollIndicator } from '../../../hooks/useScrollIndicator';
import { ArrowLeft, CheckCircle, Trash2 } from 'lucide-react';
import { IntlMessages } from '../../../helper/langHelper';

const DocumentUpload = ({
  title,
  fileName: initialFileName,
  pdfId,
  applicationId,
  documentId,
  onDocumentUploaded,
  onDocumentDeleted,
  uploadDocId,
  pdfType,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState(initialFileName);
  const [isUploadSuccess, setIsUploadSuccess] = useState(!!documentId);
  const [currentDocumentId, setCurrentDocumentId] = useState(documentId);
  const [errorUpload, setErrorUpload] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setCurrentDocumentId(documentId);
    setIsUploadSuccess(!!documentId);
    setUploadedFileName(initialFileName);
  }, [documentId, initialFileName]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFileName(file.name);
    setUploadProgress(30);
    setErrorUpload('');

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64File = reader.result.split(',')[1];

      const payload = {
        applicationId,
        objectId: pdfId,
        file: base64File,
        pdfType,
      };

      try {
        const interval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev < 90) return prev + 10;
            clearInterval(interval);
            return prev;
          });
        }, 500);

        const response = await uploadPdf(payload, (progress) => {
          setUploadProgress(progress);
        });

        clearInterval(interval);
        setUploadProgress(100);
        setUploadStatus(<IntlMessages id="document.upload.success" />);
        setIsUploadSuccess(true);

        // Update local documentId from response
        if (response?.documentId) {
          setCurrentDocumentId(response.documentId);

          // Notify parent to update the specific document in state
          if (onDocumentUploaded) {
            onDocumentUploaded(pdfId, response.documentId, file.name);
          }
        }
      } catch (error) {
        setErrorUpload(`Error: ${error.message}`);
        setUploadProgress(0);
        setIsUploadSuccess(false);
      }
    };

    reader.onerror = () => {
      setErrorUpload(<IntlMessages id="document.upload.read.error" />);
      setIsUploadSuccess(false);
    };
  };

  const handleDeleteDocument = async () => {
    if (!uploadDocId || !applicationId) return;

    const confirmDelete = window.confirm(`Are you sure you want to delete this document?`);
    if (!confirmDelete) return;

    setIsDeleting(true);

    try {
      await deleteUploadedDoc({
        objectId: uploadDocId,
        applicationId: applicationId,
        pdfType,
      });

      // Reset local state
      setIsUploadSuccess(false);
      setCurrentDocumentId(null);
      setUploadedFileName('File not uploaded');
      setUploadStatus('');
      setUploadProgress(0);
      setErrorUpload('');

      // Notify parent to update state
      if (onDocumentDeleted) {
        onDocumentDeleted(pdfId);
      }
    } catch (error) {
      setErrorUpload(`Delete failed: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewFile = () => {
    const url = `https://urban-compass-docs-ap-southeast-2-stage.s3.ap-southeast-2.amazonaws.com/urban-compass/${applicationId}/${currentDocumentId}`;
    window.open(url, '_blank');
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Title>{title}</Title>
        <ActionContainer>
          {isUploadSuccess && <CheckCircle color="green" size={16} />}
          {(currentDocumentId || isUploadSuccess) && (
            <DeleteButton onClick={handleDeleteDocument} disabled={isDeleting}>
              <Trash2 size={16} />
            </DeleteButton>
          )}
        </ActionContainer>
      </div>

      {uploadedFileName ? (
        <FileName>{uploadedFileName}</FileName>
      ) : (
        <FileName>
          <IntlMessages id="document.upload.filename.placeholder" />
        </FileName>
      )}

      {currentDocumentId ? (
        <div style={{ display: 'flex', gap: '20px' }}>
          <UploadButton htmlFor={title}>
            <IntlMessages id="document.upload.upload" />
          </UploadButton>
          <ViewButton onClick={handleViewFile}>
            <IntlMessages id="document.upload.view" />
          </ViewButton>
        </div>
      ) : title === 'Title Report' ? (
        <div style={{ display: 'flex', gap: '20px' }}>
          <UploadButton htmlFor={title}>
            <IntlMessages id="document.upload.upload" />
          </UploadButton>
          <PurchaseButton onClick={() => setModalOpen(true)}>
            <IntlMessages id="document.upload.purchase" />
          </PurchaseButton>
        </div>
      ) : (
        <UploadButton htmlFor={title}>
          <IntlMessages id="document.upload.upload" />
        </UploadButton>
      )}

      <Input type="file" id={title} onChange={handleFileUpload} accept=".pdf" />

      {uploadProgress > 0 && uploadProgress < 100 && (
        <ProgressBar>
          <Progress style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease-in-out' }} />
        </ProgressBar>
      )}

      {uploadProgress === 100 && uploadStatus && <StatusMessage>{uploadStatus}</StatusMessage>}
      {uploadProgress !== 100 && errorUpload && (
        <StatusMessage status="error">{errorUpload}</StatusMessage>
      )}

      {isDeleting && (
        <StatusMessage status="error">
          <IntlMessages id="document.upload.deleting" />
        </StatusMessage>
      )}

      <PopUp isModalOpen={isModalOpen} handleCloseModal={() => setModalOpen(false)} />
    </Card>
  );
};

const DocumentUploader = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [isDocList, setIsDocList] = useState(false);
  const containerRef = useRef(null);

  const location = useLocation();
  const applicationId = location?.state?.applicationId || getItem('applicationId');

  const { showScrollIndicator, scrollToBottom } = useScrollIndicator(containerRef, docs);

  useEffect(() => {
    if (applicationId) {
      fetchPdfList();
    }
  }, [applicationId]);

  const fetchPdfList = async () => {
    try {
      setIsDocList(true);
      const response = await getPdfListForUpload({ applicationId });
      setIsDocList(false);
      setDocs(response || []);
    } catch (error) {
      setIsDocList(false);
    }
  };

  // Handle document upload - update state directly without refetching
  const handleDocumentUploaded = (pdfId, documentId, fileName) => {
    setDocs((prevDocs) => {
      const newDocs = { ...prevDocs };

      // Update pdfList
      if (newDocs.pdfList) {
        newDocs.pdfList = newDocs.pdfList.map((item) =>
          item._id === pdfId ? { ...item, documentId, fileName } : item
        );
      }

      // Update overlayList
      if (newDocs.overlayList) {
        newDocs.overlayList = newDocs.overlayList.map((item) =>
          item._id === pdfId ? { ...item, documentId, fileName } : item
        );
      }

      return newDocs;
    });
  };

  // Handle document deletion - update state directly without refetching
  const handleDocumentDeleted = (pdfId) => {
    setDocs((prevDocs) => {
      const newDocs = { ...prevDocs };

      // Update pdfList
      if (newDocs.pdfList) {
        newDocs.pdfList = newDocs.pdfList.map((item) =>
          item._id === pdfId ? { ...item, documentId: null, fileName: null } : item
        );
      }

      // Update overlayList
      if (newDocs.overlayList) {
        newDocs.overlayList = newDocs.overlayList.map((item) =>
          item._id === pdfId ? { ...item, documentId: null, fileName: null } : item
        );
      }

      return newDocs;
    });
  };

  const handleNavigateToReportReview = () => {
    navigate('/reportReview');
  };

  const handleBack = () => {
    window.history.back();
  };

  const isButtonDisabled = React.useMemo(() => {
    const allItems = [...(docs?.pdfList || []), ...(docs?.overlayList || [])];
    return allItems.length > 0 && allItems.some((item) => item.documentId === null);
  }, [docs?.pdfList, docs?.overlayList]);

  const totalDocuments = (docs?.pdfList?.length || 0) + (docs?.overlayList?.length || 0);

  return (
    <>
      <Main ref={containerRef}>
        <Heading>
          <IntlMessages id="document.upload.heading" />
          <span>
            <IntlMessages id="document.upload.total" values={{ total: totalDocuments }} />
          </span>
        </Heading>
        {isDocList ? (
          <>
            <SkeletonLoader cardCount={3} />
          </>
        ) : (
          <>
            <Container className="upload_section">
              {docs?.pdfList?.length > 0 &&
                docs?.pdfList?.map((item) => (
                  <DocumentUpload
                    key={item.id}
                    title={item.pdfId.pdfTitle}
                    fileName={item?.documentId ? item?.documentId : '(supported pdf only)'}
                    applicationId={applicationId}
                    pdfId={item._id}
                    documentId={item?.documentId}
                    onDocumentUploaded={handleDocumentUploaded}
                    onDocumentDeleted={handleDocumentDeleted}
                    uploadDocId={item?._id}
                    pdfType="G"
                  />
                ))}

              {docs?.overlayList?.length > 0 &&
                docs?.overlayList?.map((overlay) => (
                  <DocumentUpload
                    key={overlay.id}
                    title={overlay.overlay}
                    fileName="(supported pdf only)"
                    applicationId={applicationId}
                    pdfId={overlay._id}
                    documentId={overlay?.documentId}
                    onDocumentUploaded={handleDocumentUploaded}
                    onDocumentDeleted={handleDocumentDeleted}
                    uploadDocId={overlay?._id}
                    pdfType="O"
                  />
                ))}
            </Container>

            <ScrollIndicator
              isVisible={showScrollIndicator}
              onClick={scrollToBottom}
              text={<IntlMessages id="document.scroll.more" />}
            />

            <Actions>
              <Button onClick={handleBack} aria-label="Go back to previous step">
                <ArrowLeft /> <IntlMessages id="button.back" />
              </Button>
              <RivewAndLoadgeButton
                onClick={handleNavigateToReportReview}
                disabled={isButtonDisabled}
              >
                <IntlMessages id="button.review.lodge" />
              </RivewAndLoadgeButton>
            </Actions>
          </>
        )}
      </Main>
    </>
  );
};

export default DocumentUploader;
